import { Button, Input, Select, TableProps } from "antd";
import { capitalize } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../../components/ui/CustomTable";
import { API_URL } from "../../../../constants/apiUrl";
import { APP_API } from "../../../../constants/constants";
import {
  useGetBatchLogQuery,
  useGetSingleBatchQuery,
} from "../../../../features/portal/batch/batchApi";
import { useGetAllDisbursementsQuery } from "../../../../features/portal/disbursementPayment/disbursementPaymentApi";
import useDownloadEmployeeXlsx from "../../../../hooks/useDownloadExcelWithApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import { formatDate, formatTime } from "../../../../lib/time";
import { GetSingleUserWalletRoleDataType } from "../../../../types/portal/companyOnboard";
import {
  GetAllDisbursementDataParamsType,
  GetAllDisbursementDataType,
} from "../../../../types/portal/disbursementPayment";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";
import ViewPaymentLogModal from "./ViewPaymentLogModal";

type Props = {
  getSingleUserWalletRoleData?: GetSingleUserWalletRoleDataType;
};

export default function PaidListTable({ getSingleUserWalletRoleData }: Props) {
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const { open, handleOpen, handleClose } = useModal<
    undefined,
    "CONFIRM_FOR_APPROVAL"
  >();
  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllDisbursementDataParamsType>({
    defaultLimit: 1000,
  });
  const { user } = useAppSelector((state) => state.auth);
  const { id } = useParams();

  // const {
  //   data: getSingleUserWalletRoleData,
  //   isError: getSingleUserWalletRoleIsError,
  //   error: getSingleUserWalletRoleError,
  //   isLoading: getSingleUserWalletRoleIsLoading,
  // } = useGetSingleUserWalletRoleQuery(
  //   {
  //     walletId: walletId ? Number(walletId) : undefined,
  //     userId: user?.data?.id,
  //   },
  //   { skip: !walletId || !user?.data?.id }
  // );

  const roleCheck = localStorage.getItem("project_role");

  const {
    isLoading: isVendorLoading,
    isError: isVendorDetailsError,
    error: vendorDetailsError,
    data: vendorDetails,
    isFetching,
    refetch,
  } = useGetAllDisbursementsQuery(
    {
      params: {
        ...filter,
        batch_id: id,
        company_id: user?.data?.company_id,
        page,
        limit,
        search: keyword,
      },
    },
    {
      skip: !user?.data?.company_id || !id,
    }
  );
  const { data: singleBatchData } = useGetSingleBatchQuery(id);
  const {
    isError: isBatchLogError,
    error: batchLogError,
    isLoading: isBatchLogLoading,
    data: batchLogData,
    refetch: batchLogRefetch,
  } = useGetBatchLogQuery(
    {
      params: {
        id: id,
      },
    },
    {
      skip: !id,
    }
  );

  const singleBatch = singleBatchData?.data;
  useError(isVendorDetailsError, vendorDetailsError, "Get payment data fail");
  // useError(
  //   getSingleUserWalletRoleIsError,
  //   getSingleUserWalletRoleError,
  //   "Get single user wallet role failed"
  // );
  useError(isBatchLogError, batchLogError, "Get batch log data failed");

  const columns: TableProps<GetAllDisbursementDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Employee ID</div>,
      dataIndex: "employee_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Employee Name</span>,
      dataIndex: "employee_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Department</span>,
      align: "center",
      dataIndex: "department",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Designation</span>,
      dataIndex: "designation",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Amount (BDT)</span>,
      dataIndex: "amount",
      align: "center",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Payment Type</span>,
      align: "center",
      dataIndex: "disbursement_type",
      render: (val: string) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "N/A",
    },
    {
      title: <span className="whitespace-nowrap">Wallet Number</span>,
      dataIndex: "wallet_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Bank Name</span>,
      dataIndex: "bank_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Title</span>,
      dataIndex: "account_title",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Account Number</span>,
      dataIndex: "account_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Branch Name</span>,
      dataIndex: "branch_name",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Routing Number</span>,
      dataIndex: "routing_number",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction ID</span>,
      dataIndex: "transaction_id",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Transaction Date</span>,
      dataIndex: "transaction_date",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Created At</span>,
      align: "center",
      dataIndex: "created_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Updated At</span>,
      align: "center",
      dataIndex: "updated_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Narration</span>,
      dataIndex: "narration",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Remarks</span>,
      dataIndex: "remarks",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Current Stage</div>,
      dataIndex: "current_stage",
      align: "center",
      render: (val) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700`}
        >
          {val.charAt(0).toUpperCase() + val.slice(1)}
        </span>
      ),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Status</div>,
      dataIndex: "status",
      align: "center",
      render: (val: "pending" | "success" | "failed" | "reject" | string) => {
        const statusClasses: Record<string, string> = {
          pending: "bg-yellow-100 text-yellow-700",
          success: "bg-green-100 text-green-700",
          failed: "bg-red-100 text-red-700",
          reject: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusClasses[val] || "bg-yellow-100 text-yellow-700"
            }`}
          >
            {capitalize(val)}
          </span>
        );
      },
    },
    // {
    //   title: <div className="whitespace-nowrap !w-[110px]">Action Log</div>,
    //   align: "center",
    //   render: (record: any) => (
    //     <Button onClick={() => handleOpen({ type: "VIEW" })}>View</Button>
    //   ),
    // },
  ];

  const totalAmount = vendorDetails?.data?.data?.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0
  );

  const handleDownload = () => {
    downloadXlsx(
      APP_API + API_URL.getAllDisbursements,
      {
        ...filter,
        page: page,
        search: keyword,
        skip: 0,
        limit: 1000000000000000,
        batch_id: id,
        company_id: user?.data?.company_id,
      },
      `Salary_Disbursment_${singleBatch?.batchName}_Paid List_${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "data",
      (item: GetAllDisbursementDataType["data"]["data"][0]) => {
        return {
          "Employee ID": item?.employee_id || "-",
          "Employee Name": item?.employee_name || "-",
          Department: item?.department || "-",
          Designation: item?.designation || "-",
          Amount:
            item?.amount !== undefined && item?.amount !== null
              ? Number(item.amount).toLocaleString("en-IN")
              : "-",
          "Payment Type": capitalize(item?.disbursement_type) || "-",
          "Wallet Number": item?.wallet_number || "-",
          "Bank Name": item?.bank_name || "-",
          "Account Title": item?.account_title || "-",
          "Account Number": item?.account_number || "-",
          "Branch Name": item?.branch_name || "-",
          "Routing Number": item?.routing_number || "-",
          "	Transaction ID": item?.transaction_id || "-",
          "Transaction Date": item?.transaction_date || "-",
          "Created At": item?.created_at
            ? `${formatDate(item?.created_at)}, ${formatTime(item?.created_at)}`
            : "-",
          "Updated At": item?.updated_at
            ? `${formatDate(item?.updated_at)}, ${formatTime(item?.updated_at)}`
            : "-",
          Narration: item.narration || "-",
          Remarks: item?.remarks || "-",
          "Current Stage": capitalize(item?.current_stage) || "-",
          Status: capitalize(item?.status) || "-",
        };
      }
    );
  };

  // useEffect(() => {
  //   if (getSingleUserWalletRoleIsError) {
  //     setTimeout(() => {
  //       navigate("/admin/salary-disbursement/batch");
  //     }, 1500);
  //   }
  // }, [getSingleUserWalletRoleIsError, navigate]);

  return (
    <>
      {open?.type === "VIEW" && (
        <ViewPaymentLogModal
          title="Send to Approver"
          subTitle="Are you sure you want to send payment data to approver"
          buttonName="Send"
          open={open?.open}
          handleClose={handleClose}
          action
          buttonBgDeep
          loading={false}
          batchLogData={batchLogData}
        />
      )}

      <div>
        <div className="flex justify-between items-center mb-3">
          <div className="justify-start text-zinc-700 text-xl font-semibold font-['Inter'] leading-loose">
            Paid List
          </div>
          <div className="gap-3 flex justify-end items-center">
            {/* <Button onClick={() => handleOpen({ type: "VIEW" })}>
              Action Log
            </Button> */}

            <Button
              disabled={!vendorDetails?.data?.data?.length}
              onClick={handleDownload}
              loading={excelLoading}
              className="h-9 px-3.5 py-2 !bg-white rounded-md outline outline-1 outline-offset-[-1px] outline-blue-500 inline-flex justify-center items-center gap-2.5 overflow-hidden disabled:cursor-not-allowed"
            >
              <span className="text-blue-600 text-sm font-semibold">
                Download
              </span>
            </Button>
          </div>
        </div>
        <CustomTable
          loading={isVendorLoading || isBatchLogLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={vendorDetails?.data?.count}
          dataSource={addKeyInArray(vendorDetails?.data?.data)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          // rowSelection={{
          //   type: "checkbox",
          //   selectedRowKeys: selectedData,
          //   onChange: (key) => {
          //     setSelectedData(key as string[]);
          //   },
          //   getCheckboxProps: (record) => {
          //     return {
          //       disabled: record?.status === "invited",
          //     };
          //   },
          //   columnWidth: 40,
          // }}
          filterSection={
            <>
              {getSingleUserWalletRoleData?.data?.role === "maker" &&
                roleCheck === "User" && (
                  <div className="px-3 py-1 bg-violet-50 rounded-[100px] inline-flex justify-center items-center gap-2.5">
                    <div className="justify-start text-blue-600 text-base font-medium">
                      Total Amount: ৳{" "}
                      {Number(totalAmount)
                        .toFixed(2)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"}
                    </div>
                  </div>
                )}

              <Input
                suffix={<SearchIcon />}
                placeholder="Search ID, Name, Wallet, Ac. Number"
                style={{ width: 317 }}
                allowClear
                onChange={handleSearch}
              />

              <Select
                placeholder="Payment Type"
                showSearch
                options={[
                  { label: "Bank", value: "bank" },
                  { label: "Wallet", value: "wallet" },
                ]}
                value={filter?.payment_type ?? undefined}
                onChange={(value) => handleFilter("payment_type", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                // style={{ width: "100%" }}
                allowClear
                optionFilterProp="label"
              />

              <Select
                placeholder="Status"
                showSearch
                options={[
                  { label: "Pending", value: "pending" },
                  {
                    label: "Success",
                    value: "success",
                  },
                  { label: "Failed", value: "failed" },
                  { label: "Reject", value: "reject" },
                ]}
                value={filter?.status ?? undefined}
                onChange={(value) => handleFilter("status", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                // style={{ width: "100%" }}
                allowClear
                optionFilterProp="label"
              />
            </>
          }
        />
      </div>
    </>
  );
}
